import React from 'react';
import { ArrowRight, Check, Clock, Users, MessageSquare, ChevronRight } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function FeedbackWorkshop() {
  const {
    language
  } = useLanguage();
  const en = {
    badge: 'Feedback',
    title: 'Feedback Culture',
    titleHighlight: 'Workshop',
    intro1: 'Most feedback culture initiatives produce more feedback. What they rarely produce is better feedback — or better conditions for people to actually learn and develop.',
    intro2: 'This workshop takes the research seriously. It works through why so much feedback practice is neurologically counterproductive, what the evidence says actually helps people develop, and how teams and organisations can build something more honest and more useful than the standard performance review cycle.',
    heroCta: 'Get in touch',
    signsTitle: 'This workshop is for you if:',
    signs: ['Your team or organisation has a feedback culture that feels performative rather than genuinely useful', 'Managers are avoiding feedback conversations because they feel awkward or risky', 'You want to understand what the research actually says before building another feedback system', 'You\'ve noticed that more feedback isn\'t producing more development'],
    whatTitle: 'What the workshop covers',
    whatItems: ['Why the standard feedback model is neurologically at odds with how people actually learn', 'The difference between evaluation and attention — and why it matters', 'What appreciative feedback actually is, and how it differs from praise', 'How to have difficult conversations without the defensive response', 'What feedback tells you about the giver — and how to use that information', 'Practical tools your team can use the following week'],
    formatsTitle: 'Formats',
    formats: [{
      name: 'Half Day',
      duration: '3-4 hours',
      desc: 'The core argument and practical tools. Works as a standalone session for leadership teams or as part of a broader development programme.',
      ideal: 'Leadership teams, HR teams, people managers'
    }, {
      name: '3-Session Series',
      duration: '3 x 2 hours online',
      desc: 'The full series — more time to practice, reflect and apply between sessions. Includes individual exercises between sessions and peer feedback practice.',
      ideal: 'Teams wanting sustained culture change rather than a one-off'
    }],
    articleNote: 'The full argument behind this workshop is laid out in the On Feedback series on Substack — five articles working through the research and what to do with it.',
    ctaTitle: "Let's talk",
    ctaDesc: "Tell me about your situation and we'll figure out which format fits.",
    ctaCta: 'Get in touch'
  };
  const is = {
    badge: 'Endurgjöf',
    title: 'Feedback-menning',
    titleHighlight: 'Vinnustofa',
    intro1: 'Flestar uppbyggingar á endurgjöfarmenningu skila meiri endurgjöf. Hvað þær skila sjaldan er betri endurgjöf - eða betri aðstæður fyrir fólk til að læra og þroskast.',
    intro2: 'Þessi vinnustofa tekur rannsóknirnar alvarlega. Hún fer yfir hvers vegna mikid af endurgjöfarmenningu er taugafræðilega gagnslítt, hvað gögnin segja um hvað hjálpar fólki að þroskast, og hvernig teymi og stofnanir geta byggð eitthvað heiðarlegra og gagnlegra en hefbðbundið mat- og endurgjöfarferlið.',
    heroCta: 'Hafa samband',
    signsTitle: 'Þessi vinnustofa hentar þér ef:',
    signs: ['Teymið þít t eða stofnanin hefur endurgjöfarmenning sem finnst þyðja frekar en vera gagnleg', 'Stjórnendur forfast endurgjöfarsamtöl vegna þess að þær þykjast kjánlegar eða áhættusam ar', 'Þú vilt skilja hvað rannsóknirnar segja í raun áður en þú byggir annars endurgjöfarkerfi', 'Þú hefur tekið eftir því að meiri endurgjöf er ekki að skila meiri þróun'],
    whatTitle: 'Hvað vinnustofan fer yfir',
    whatItems: ['Hvers vegna hefbðbundið endurgjöfarlikan er taugafræðilega í ósamræmi við hvernig fólk lærir', 'Munurinn á mati og athygli - og hvers vegna hann skiptir máli', 'Hvað jákvæð endurgjöf er raunverulega, og hvernig hún er ólík hrósi', 'Hvernig á að hafa erfið samtöl án varnarvia', 'Hvað endurgjöf segir þér um gefandann - og hvernig á að nota þær upp lýsingarnar', 'Hagnýt verkfæri sem teymið getur notað næstu viku'],
    formatsTitle: 'Snið',
    formats: [{
      name: 'Hálfur dagur',
      duration: '3-4 klst',
      desc: 'Kærna rókin og hagný verkfæri. Virkar sem staðlæg lota fyrir forystu teymi eða hluti af stærra þróunarkáti.',
      ideal: 'Forystu teymi, HR teymi, stjórnendur'
    }, {
      name: '3 lotur',
      duration: '3 x 2 klukkustundir á netinu',
      desc: 'Full röðin - meira tima til að æfa, endurspegla og beita á milli lota. Innifaldar einstaklingsverkefni á milli lota og jæningjafeedback.',
      ideal: 'Teymi sem vilja sjálfbæra menningarbrótingu'
    }],
    articleNote: 'Full rökstuðningurinn bak við þessa vinnustofu er settur fram í Um endurgjöf röðinni á Substack - fimm greinar sem fara í gegnum rannsóknirnar og hvað á að gera við þær.',
    ctaTitle: 'Við skulum tala saman',
    ctaDesc: 'Segðu mér frá stöðunni og við finnum út hvað hentar.',
    ctaCta: 'Hafa samband'
  };
  const c = language === 'is' ? is : en;
  return <PageTransition data-id="element-1944">
      <div className="pt-12 pb-24" data-id="element-1945">
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20 text-center" data-id="element-1946">
          <Animate data-id="element-1947">
            <span className="inline-block px-4 py-2 bg-coral/10 text-coral font-medium rounded-full text-sm mb-6" data-id="element-1948">{c.badge}</span>
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-id="element-1949">{c.title} <span className="text-coral" data-id="element-1950">{c.titleHighlight}</span></h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-4" data-id="element-1951">{c.intro1}</p>
            <p className="text-lg text-gray-500 leading-relaxed mb-8" data-id="element-1952">{c.intro2}</p>
            <Link to="/contact" data-id="element-1953"><Button rightIcon={<ArrowRight size={18} data-id="element-1955" />} data-id="element-1954">{c.heroCta}</Button></Link>
          </Animate>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1956">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1957">
            <Animate data-id="element-1958">
              <div data-id="element-1959">
                <h2 className="text-xl font-bold text-charcoal mb-5" data-id="element-1960">{c.signsTitle}</h2>
                <div className="space-y-3" data-id="element-1961">
                  {c.signs.map((s, i) => <div key={i} className="flex items-start gap-3" data-id="element-1962">
                      <div className="mt-1 p-1 rounded-full bg-coral/10 text-coral flex-shrink-0" data-id="element-1963"><Check size={12} strokeWidth={3} data-id="element-1964" /></div>
                      <span className="text-gray-600 leading-relaxed" data-id="element-1965">{s}</span>
                    </div>)}
                </div>
              </div>
            </Animate>
            <Animate delay={0.1} data-id="element-1966">
              <div data-id="element-1967">
                <h2 className="text-xl font-bold text-charcoal mb-5" data-id="element-1968">{c.whatTitle}</h2>
                <div className="space-y-3" data-id="element-1969">
                  {c.whatItems.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-1970">
                      <div className="mt-1 p-1 rounded-full bg-coral/10 text-coral flex-shrink-0" data-id="element-1971"><Check size={12} strokeWidth={3} data-id="element-1972" /></div>
                      <span className="text-gray-600 leading-relaxed" data-id="element-1973">{item}</span>
                    </div>)}
                </div>
              </div>
            </Animate>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-1974">
          <Animate className="mb-8" data-id="element-1975"><h2 className="text-3xl font-bold text-charcoal" data-id="element-1976">{c.formatsTitle}</h2></Animate>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1977">
            {c.formats.map((f, i) => <Animate key={i} delay={i * 0.1} data-id="element-1978">
                <Card className="p-8 h-full border-2 border-coral/20" data-id="element-1979">
                  <h3 className="font-bold text-2xl text-charcoal mb-2" data-id="element-1980">{f.name}</h3>
                  <div className="flex items-center gap-2 mb-4 text-coral" data-id="element-1981"><Clock size={16} data-id="element-1982" /><span className="text-sm font-medium" data-id="element-1983">{f.duration}</span></div>
                  <p className="text-gray-600 leading-relaxed mb-4" data-id="element-1984">{f.desc}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-400" data-id="element-1985"><Users size={14} data-id="element-1986" /><span data-id="element-1987">{f.ideal}</span></div>
                </Card>
              </Animate>)}
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-16" data-id="element-1988">
          <Animate data-id="element-1989">
            <Card className="p-6 border-l-4 border-coral bg-coral/5" data-id="element-1990">
              <div className="flex items-start gap-3" data-id="element-1991">
                <MessageSquare size={20} className="text-coral flex-shrink-0 mt-0.5" data-id="element-1992" />
                <p className="text-gray-700" data-id="element-1993">{c.articleNote}{' '}
                  <a href="https://helgig.substack.com" target="_blank" rel="noopener noreferrer" className="text-coral font-semibold hover:underline" data-id="element-1994">Substack <ChevronRight size={14} className="inline" data-id="element-1995" /></a>
                </p>
              </div>
            </Card>
          </Animate>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1996">
          <Animate data-id="element-1997">
            <Card className="p-10 bg-gradient-to-br from-coral/5 to-teal/5 border-0 text-center" data-id="element-1998">
              <h2 className="text-2xl font-bold mb-3" data-id="element-1999">{c.ctaTitle}</h2>
              <p className="text-gray-500 mb-6 max-w-lg mx-auto" data-id="element-2000">{c.ctaDesc}</p>
              <Link to="/contact" data-id="element-2001"><Button variant="primary" rightIcon={<ArrowRight size={18} data-id="element-2003" />} data-id="element-2002">{c.ctaCta}</Button></Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}